import { type MaybeRefOrGetter, toValue } from 'vue'

import { useEventListener } from '@vueuse/core'

import { EDITABLE_TAGS, MODIFIERS } from './constants'
import type { ModifierKey, Shortcut } from './types'

/**
 * Letters safe across QWERTY, AZERTY, and QWERTZ layouts (same physical key
 * position / event.code in all three):
 * B, C, D, E, F, G, H, I, J, K, L, N, O, P, R, S, T, U, V, X
 *
 * Letters that DIFFER across layouts (avoid for new shortcuts):
 * A, Q, W, Z, Y, M
 *
 * When adding a new shortcut with modifiers, pick a letter from the safe set
 * to ensure it works regardless of the user's keyboard layout.
 */

/**
 * Checks whether the given target is an editable element.
 *
 * @param target EventTarget | null - The target to check.
 * @returns boolean - Whether the target is editable.
 */
function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }

  return EDITABLE_TAGS.has(target.tagName) || target.isContentEditable
}

/**
 * Registers global keyboard shortcuts for accessibility navigation.
 *
 * @param shortcuts - List of shortcuts to listen for.
 */
export function useKeyboardShortcuts(shortcuts: MaybeRefOrGetter<Shortcut[]>) {
  // Client side only.
  if (!import.meta.client) {
    return
  }

  useEventListener(window, 'keydown', (event: KeyboardEvent) => {
    const theShortcut = toValue(shortcuts).find((shortcut) => {
      if (shortcut.modifiers.length === 0) {
        // Shortcuts without modifiers match on event.key (the produced
        // character) rather than event.code (the physical key). This ensures
        // correct behavior across keyboard layouts — e.g. on a French AZERTY
        // Mac keyboard, "/" is produced with Shift+Slash, so matching on
        // event.code would incorrectly trigger on plain Slash (which produces
        // ":").
        if (isEditable(event.target)) {
          return false
        }

        return shortcut.keys.includes(event.key)
      }

      // Shortcuts with modifiers match on event.code (the physical key)
      // rather than event.key. On macOS, the Option (Alt) key acts as a
      // dead key and transforms the produced character — e.g. Option+H
      // produces "˙" instead of "h". Matching on event.code avoids this
      // by identifying the physical key regardless of modifier-induced
      // character transformations.
      const modifiersMatch = shortcut.modifiers.every(
        (modifier) => event[modifier],
      )
      const noExtraModifiers = (Object.keys(MODIFIERS) as ModifierKey[]).every(
        (modifier) => shortcut.modifiers.includes(modifier) === event[modifier],
      )

      if (!modifiersMatch || !noExtraModifiers) {
        return false
      }

      return shortcut.codes.includes(event.code)
    })

    if (theShortcut) {
      event.preventDefault()
      theShortcut.action()
    }
  })
}
